import { FormEvent, useCallback, useMemo } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
} from "@tabler/icons-react";
import type {
  DeliveryRecallResult,
  DeliveryState,
  RecallResult,
  RecallServiceError,
  RecallState,
} from "../../services/reportDeliveryRecall.service";
import { formatRecallScheduledTime } from "./RecallReportButton";

interface RecallReportModalProps {
  opened: boolean;
  onClose: () => void;
  state: RecallState | undefined;
  result: RecallResult | null;
  error: RecallServiceError | null;
  isSubmitting: boolean;
  isReconciling: boolean;
  onSubmit: (reason: string) => Promise<unknown>;
  onCheckNow: () => Promise<void>;
  caseNumber?: string;
}

interface RecallReasonForm {
  reason: string;
}

function displayValue(value: string | null | undefined): string {
  return value?.trim() || "Not available";
}

function consequenceLabel(delivery: DeliveryRecallResult): string {
  const labels: Record<DeliveryRecallResult["result"], string> = {
    recalled: "Recalled",
    already_recalled: "Already recalled",
    dispatch_started: "Sending",
    already_sent: "Delivered or sent",
    failed: "Failed",
    cancelled: "Administratively cancelled",
    superseded: "Automatically superseded",
    legacy_unbound: "Report version unavailable",
    unavailable: "Unavailable",
  };
  return labels[delivery.result];
}

function deliveryStatusLabel(status: DeliveryState["status"]): string {
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function RecallReportModal({
  opened,
  onClose,
  state,
  result,
  error,
  isSubmitting,
  isReconciling,
  onSubmit,
  onCheckNow,
  caseNumber,
}: RecallReportModalProps) {
  const form = useForm<RecallReasonForm>({
    initialValues: { reason: "" },
    validateInputOnBlur: true,
    validate: {
      reason: (value) => {
        const trimmed = value.trim();
        if (trimmed.length > 1000) {
          return "Keep the recall reason to 1,000 characters or fewer.";
        }
        return null;
      },
    },
  });

  const reportIdentity = useMemo(() => {
    const visibleCaseNumber = caseNumber ?? "Current case";
    const version = state?.reportVersion ?? "Not available";
    return `${visibleCaseNumber} • Report version ${version}`;
  }, [caseNumber, state?.reportVersion]);

  const handleSubmit = useCallback(
    async (values: RecallReasonForm) => {
      await onSubmit(values.reason.trim());
    },
    [onSubmit]
  );
  const handleFormSubmit = useMemo(
    () => form.onSubmit(handleSubmit),
    [form, handleSubmit]
  );
  const handleClose = useCallback(() => {
    if (isSubmitting || isReconciling) return;
    form.reset();
    onClose();
  }, [form, isReconciling, isSubmitting, onClose]);
  const handleCheckNow = useCallback(() => {
    void onCheckNow();
  }, [onCheckNow]);
  const deliveryRows = useMemo(
    () => state?.deliveries ?? [],
    [state?.deliveries]
  );
  const resultRows = useMemo(() => result?.deliveries ?? [], [result]);
  const resultTitle = useMemo(() => {
    if (!result) return null;
    if (result.outcome === "recalled") return "Report recalled";
    if (result.outcome === "already_recalled") return "Report already recalled";
    if (result.outcome === "partial") {
      return "Partial recall — immediate follow-up required";
    }
    return "Report could not be recalled";
  }, [result]);
  const resultColor = useMemo(
    () => (result?.outcome === "recalled" ? "green" : "orange"),
    [result?.outcome]
  );
  const resultMessage = useMemo(() => {
    if (!result) return null;
    if (result.outcome === "recalled") {
      return `${result.recalledCount} deliver${
        result.recalledCount === 1 ? "y was" : "ies were"
      } stopped. Nothing was sent.`;
    }
    if (result.outcome === "already_recalled") {
      return "These deliveries had already been recalled. Nothing new was changed.";
    }
    if (result.outcome === "partial") {
      return `${result.recalledCount} stopped; ${result.notStoppedCount} could not be stopped. Follow the report-correction escalation procedure now.`;
    }
    return "No delivery could be stopped. Follow the report-correction escalation procedure now.";
  }, [result]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Recall queued report?"
      size="xl"
      centered
      closeOnClickOutside={!isSubmitting && !isReconciling}
      closeOnEscape={!isSubmitting && !isReconciling}
      returnFocus
    >
      <Stack gap="md">
        <Text fw={600}>{reportIdentity}</Text>

        {result ? (
          <>
            <Alert
              icon={
                result.outcome === "recalled" ? (
                  <IconCircleCheck size={18} />
                ) : (
                  <IconAlertTriangle size={18} />
                )
              }
              color={resultColor}
              variant="light"
              title={resultTitle}
              role={
                result.outcome === "recalled" ||
                result.outcome === "already_recalled"
                  ? "status"
                  : "alert"
              }
            >
              {resultMessage}
            </Alert>
            {resultRows.length > 0 && (
              <>
                <Table.ScrollContainer minWidth={560} visibleFrom="sm">
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Recipient</Table.Th>
                        <Table.Th>Method</Table.Th>
                        <Table.Th>Scheduled</Table.Th>
                        <Table.Th>Outcome</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {resultRows.map((delivery) => (
                        <Table.Tr key={delivery.queueItemId}>
                          <Table.Td>
                            <Stack gap={0}>
                              <Text size="sm">
                                {displayValue(delivery.recipientName)}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {displayValue(delivery.destination)}
                              </Text>
                            </Stack>
                          </Table.Td>
                          <Table.Td>
                            {displayValue(delivery.deliveryMethodName)}
                          </Table.Td>
                          <Table.Td>
                            {formatRecallScheduledTime(delivery.scheduledAt)}
                          </Table.Td>
                          <Table.Td>{consequenceLabel(delivery)}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
                <Stack gap="xs" hiddenFrom="sm">
                  {resultRows.map((delivery) => (
                    <Paper key={delivery.queueItemId} withBorder p="sm">
                      <Stack gap={4}>
                        <Text size="sm" fw={600}>
                          {displayValue(delivery.recipientName)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {displayValue(delivery.destination)}
                        </Text>
                        <Text size="sm">
                          Method: {displayValue(delivery.deliveryMethodName)}
                        </Text>
                        <Text size="sm">
                          Scheduled:{" "}
                          {formatRecallScheduledTime(delivery.scheduledAt)}
                        </Text>
                        <Text size="sm">
                          Outcome: {consequenceLabel(delivery)}
                        </Text>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </>
            )}
            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={handleClose}
                data-autofocus
                style={{ minHeight: 44 }}
              >
                Done
              </Button>
            </Group>
          </>
        ) : (
          <form onSubmit={handleFormSubmit as (event: FormEvent) => void}>
            <Stack gap="md">
              <Text size="sm">
                This attempts to stop every delivery that is still pending. A
                delivery already being sent cannot be recalled.
              </Text>

              <Table.ScrollContainer minWidth={640} visibleFrom="sm">
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Recipient</Table.Th>
                      <Table.Th>Method</Table.Th>
                      <Table.Th>Sends at</Table.Th>
                      <Table.Th>Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {deliveryRows.map((delivery: DeliveryState) => (
                      <Table.Tr key={delivery.id}>
                        <Table.Td>
                          <Stack gap={0}>
                            <Text size="sm">
                              {displayValue(delivery.recipientName)}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {displayValue(delivery.destination)}
                            </Text>
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          {displayValue(delivery.deliveryMethodName)}
                        </Table.Td>
                        <Table.Td>
                          {formatRecallScheduledTime(delivery.scheduledAt)}
                        </Table.Td>
                        <Table.Td>
                          {deliveryStatusLabel(delivery.status)}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
              <Stack gap="xs" hiddenFrom="sm">
                {deliveryRows.map((delivery: DeliveryState) => (
                  <Paper key={delivery.id} withBorder p="sm">
                    <Stack gap={4}>
                      <Text size="sm" fw={600}>
                        {displayValue(delivery.recipientName)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {displayValue(delivery.destination)}
                      </Text>
                      <Text size="sm">
                        Method: {displayValue(delivery.deliveryMethodName)}
                      </Text>
                      <Text size="sm">
                        Sends at:{" "}
                        {formatRecallScheduledTime(delivery.scheduledAt)}
                      </Text>
                      <Text size="sm">
                        Status: {deliveryStatusLabel(delivery.status)}
                      </Text>
                    </Stack>
                  </Paper>
                ))}
              </Stack>

              {error?.kind === "unknown" ? (
                <Alert
                  icon={<IconAlertCircle size={18} />}
                  color="orange"
                  title="Checking recall outcome"
                  role="alert"
                >
                  <Stack gap="sm">
                    <Text size="sm">
                      The connection was interrupted. The report may still send
                      while the system verifies the request. Do not assume it
                      was recalled.
                    </Text>
                    <Button
                      variant="light"
                      color="orange"
                      onClick={handleCheckNow}
                      loading={isReconciling}
                    >
                      Check now
                    </Button>
                  </Stack>
                </Alert>
              ) : error ? (
                <Alert
                  icon={<IconAlertCircle size={18} />}
                  color="red"
                  title="Report not recalled"
                  role="alert"
                >
                  {error.message}
                </Alert>
              ) : null}

              <Textarea
                label="Reason for recall (optional)"
                minRows={3}
                maxLength={1000}
                disabled={isSubmitting || isReconciling}
                {...form.getInputProps("reason")}
              />

              <Group justify="flex-end">
                <Button
                  variant="default"
                  onClick={handleClose}
                  disabled={isSubmitting || isReconciling}
                  data-autofocus
                  style={{ minHeight: 44 }}
                >
                  Keep Queued
                </Button>
                <Button
                  type="submit"
                  color="orange"
                  leftSection={<IconAlertTriangle size={16} />}
                  loading={isSubmitting}
                  disabled={isReconciling}
                  style={{ minHeight: 44 }}
                >
                  Recall Report
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Stack>
    </Modal>
  );
}
